import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import {
  useMediaQuery,
  Box,
  Typography,
  Avatar,
  Chip,
  Stack,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Save,
  Cancel,
  Delete,
  Person,
  Phone,
  Business,
  Badge,
  AccountBalance,
  FamilyRestroom,
  School,
  ArrowBack,
} from '@mui/icons-material';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useAuthCheck from 'hooks/useAuthCheck';
import ScrollToTop from 'components/ScrollToTop';
import FlexCenter from 'components/FlexCenter';
import FlexBetween from 'components/FlexBetween';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import PersonalInfo from './PersonalInfo';
import Contact from './Contact';
import Employment from './Employment';
import Documents from './Documents';
import Payroll from './Payroll';
import Family from './Family';
import Qualifications from './Qualifications';

/* ── validation ─────────────────────────────────────────── */

const schema = yup.object({
  employeeName: yup.string().required('Full name is required'),
  employeeCode: yup.string().required('Employee code is required'),
  workingMode: yup.string().required('Working mode is required'),
});

/* ── initial values ─────────────────────────────────────── */

const buildInitialValues = (emp) => ({
  employeeCode: emp?.employeeCode || '',
  employeeName: emp?.employeeName || '',
  shortName: emp?.shortName || '',
  gender: emp?.gender || '',
  birthday: emp?.birthday || null,
  placeOfBirth: emp?.placeOfBirth || '',
  placeOfOrigin: emp?.placeOfOrigin || '',
  marital: emp?.marital || '',
  location: emp?.location || '',
  nationality: emp?.nationality || '',
  language: emp?.language || [],
  // Contact
  address: emp?.address || '',
  city: emp?.city || '',
  state: emp?.state || '',
  zip: emp?.zip || '',
  email: emp?.email || '',
  contactTel: emp?.contactTel?.length ? emp.contactTel : [''],
  // Employment
  employeeType: emp?.employeeType || 'NON-CREW',
  rank: emp?.rank || '',
  homeBase: emp?.homeBase || '',
  department: emp?.department || '',
  division: emp?.division || '',
  deptUnit: emp?.deptUnit || '',
  agency: emp?.agency || '',
  workingMode: emp?.workingMode || 'OFFICE',
  employmentContract: emp?.employmentContract || '',
  employmentStartDate: emp?.employmentStartDate || null,
  employmentEndDate: emp?.employmentEndDate || null,
  jobGradeCode: emp?.jobGradeCode || '',
  salaryGradeCode: emp?.salaryGradeCode || '',
  jobTitles: emp?.jobTitles || [],
  // Documents
  passportType: emp?.passportType || 'P',
  passportName: emp?.passportName || '',
  firstName: emp?.firstName || '',
  middleName: emp?.middleName || '',
  surname: emp?.surname || '',
  passportNumber: emp?.passportNumber || '',
  passportIssueDate: emp?.passportIssueDate || null,
  passportExpiryDate: emp?.passportExpiryDate || null,
  passportCountry: emp?.passportCountry || '',
  nationalIdNumber: emp?.nationalIdNumber || '',
  nationalIdIssueDate: emp?.nationalIdIssueDate || null,
  nationalIdIssuePlace: emp?.nationalIdIssuePlace || '',
  taxCode: emp?.taxCode || '',

  // Payroll
  baseSalary: emp?.baseSalary || '',
  contributionObject: emp?.contributionObject || '',
  bankAccount: emp?.bankAccount || { bankName: '', accountNumber: '' },
  isNetIncome: emp?.isNetIncome || false,
  insuranceDayCalculationType: emp?.insuranceDayCalculationType || [],
  // Family
  nextKinName: emp?.nextKinName || '',
  nextKinRelsn: emp?.nextKinRelsn || '',
  nextKinContact: emp?.nextKinContact || '',
  dependents: emp?.dependents || [],
  // Qualifications
  crewQuals: emp?.crewQuals || [],
});

/* ── accordion config ───────────────────────────────────── */

const SECTIONS = [
  { key: 'personal',        icon: <Person fontSize="small" />,          label: 'Personal Information' },
  { key: 'contact',         icon: <Phone fontSize="small" />,           label: 'Contact' },
  { key: 'family',          icon: <FamilyRestroom fontSize="small" />,  label: 'Family & Dependents' },
  { key: 'employment',      icon: <Business fontSize="small" />,        label: 'Employment' },
  { key: 'payroll',         icon: <AccountBalance fontSize="small" />,  label: 'Payroll' },
  { key: 'documents',       icon: <Badge fontSize="small" />,           label: 'Documents' },
  { key: 'qualifications',  icon: <School fontSize="small" />,          label: 'Qualifications' },
];

/* ── component ───────────────────────────────────────────── */

const EmployeeDetail = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const user = useSelector((s) => s.user);
  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  
  const isMobile = useMediaQuery('(max-width:600px)');
  const scrollRef = useRef(null);
  const formikRef = useRef(null);

  const isNew = !employeeId || employeeId === 'new';

  const canEdit = isNew || hasRight(permissionMap, 'employees', 'update', false);
  const canDelete = hasRight(permissionMap, 'employees', 'delete', false);

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [isEditMode, setIsEditMode] = useState(isNew);
  const [expanded, setExpanded] = useState('personal');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchEmployee = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/employees/get/${employeeId}`);
      setEmployee(data);
    } catch (err) {
      toast.error('Failed to load employee.');
    } finally {
      setLoading(false);
    }
  }, [employeeId, isNew]);

  useEffect(() => { fetchEmployee(); }, [fetchEmployee]);

  const handleAccordion = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleSubmit = async (values) => {
    try {
      if (isNew) {
        await api.post('/employees/new', values);
        toast.success('Employee created.');
        navigate('/employees', { state: { view: 'list' } });
      } else {
        await api.put(`/employees/put/${employeeId}`, values);
        toast.success('Employee updated.');
        await fetchEmployee();
        setIsEditMode(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    }
  };

  const handleDelete = async () => {
    try {
      await api.patch('/employees/soft-delete', { ids: [employeeId] });
      toast.success('Employee deleted.');
      navigate('/employees', { state: { view: 'list' } });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed.');
    }
  };

  if (loading) {
    return (
      <FlexCenter height={LAYOUT_HEIGHT}>
        <CircularProgress />
      </FlexCenter>
    );
  }

  const initialValues = buildInitialValues(employee);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="main-container-box">
        <FlexBetween>
          <Header
            title='Employee'
            subtitle={isNew ? 'Create' : 'Update'}
          />
          
          <Stack direction="row" gap={1}>
            {isEditMode ? (
              <>
                <Tooltip title="Save" arrow>
                  <StyledButton onClick={() => formikRef.current?.submitForm()}>
                    <Save  />
                  </StyledButton>
                </Tooltip>
                {!isNew && (
                  <Tooltip title="Cancel" arrow>
                    <StyledButton onClick={() => { setIsEditMode(false); formikRef.current?.resetForm(); }}>
                      <Cancel  />
                    </StyledButton>
                  </Tooltip>
                )}
              </>
            ) : (
              <>
                {canEdit && (
                  <Tooltip title="Edit" arrow>
                    <StyledButton onClick={() => setIsEditMode(true)}>
                      <Edit  />
                    </StyledButton>
                  </Tooltip>
                )}
                {canDelete && !isNew && (
                  <Tooltip title="Delete" arrow>
                    <StyledButton onClick={() => setDeleteOpen(true)} sx={{ color: 'error.main' }}>
                      <Delete  />
                    </StyledButton>
                  </Tooltip>
                )}
              </>
            )}
            <Tooltip title="Back" arrow>
              <StyledButton onClick={() => navigate('/employees', { state: { view: 'list' } })}>
                <ArrowBack />
              </StyledButton>
            </Tooltip>
          </Stack>
        </FlexBetween>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box 
            ref={scrollRef}
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
          >
            <Formik
              innerRef={formikRef}
              enableReinitialize
              initialValues={initialValues}
              validationSchema={schema}
              onSubmit={handleSubmit}
            >
              {(formik) => {
                const { 
                  values, 
                  handleChange, 
                  handleBlur, 
                  errors, 
                  touched, 
                  setFieldValue,
                } = formik;

                /* ── profile header ── */
                const renderHeader = () => (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      gap: 2,
                      p: 2,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      mb: 2,
                    }}
                  >
                    {/* Avatar */}
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: 'primary.main',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        flexShrink: 0,
                      }}
                    >
                      {(values.employeeName || '?')
                        .trim()
                        .split(' ')
                        .slice(-2)
                        .map(word => word.charAt(0).toUpperCase())
                        .join('')
                      }
                    </Avatar>

                    {/* Identity info */}
                    <Box flex={1} minWidth={0}>
                      <Stack 
                        direction="row" 
                        flexWrap="wrap"
                        alignItems="center" 
                        gap={1} 
                      >
                        <Typography 
                          variant="h5" 
                          fontWeight="bold" 
                          noWrap={!isMobile}
                        >
                          {values.employeeName || (isNew ? 'New Employee' : '-')}
                        </Typography>
                        {values.employeeCode && (
                          <Chip 
                            label={values.employeeCode} 
                            size="small" 
                            variant="outlined" 
                          />
                        )}
                        <Chip
                          label={values.employeeType || 'NON-CREW'}
                          color={values.employeeType === 'CREW' ? 'primary' : 'default'}
                          size="small"
                        />
                        {employee && (
                          <Chip
                            label={employee.isActive ? 'Active' : 'Inactive'}
                            color={employee.isActive ? 'success' : 'error'}
                            size="small"
                          />
                        )}
                      </Stack>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ mt: 0.25 }}
                      >
                        {[
                          values.rank, 
                          values.homeBase ? `${values.homeBase}` : null,
                          values.department, 
                        ].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>

                  </Box>
                );

                const sectionProps = { 
                  values, 
                  handleChange, 
                  handleBlur, 
                  errors, 
                  touched, 
                  setFieldValue, 
                  isEditMode 
                };

                return (
                  <>
                    {renderHeader()}

                    {/* Accordion tree */}
                    {SECTIONS.map((section) => {
                      if (section.key === 'qualifications' && values.employeeType !== 'CREW') return null;

                      return (
                        <Accordion
                          key={section.key}
                          expanded={expanded === section.key}
                          onChange={handleAccordion(section.key)}
                          sx={{ 
                            mb: 0.5, 
                            '&:before': { display: 'none' }, 
                            borderRadius: '8px !important' 
                          }}
                          disableGutters
                        >
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Stack direction="row" alignItems="center" gap={1}>
                              <Box sx={{ display: 'flex', color: 'primary.main' }}>
                                {section.icon}
                              </Box>
                              <Typography variant="subtitle2" fontWeight="600">
                                {section.label}
                              </Typography>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Divider sx={{ mb: 2 }} />
                            {section.key === 'personal' && <PersonalInfo {...sectionProps} />}
                            {section.key === 'contact' && <Contact {...sectionProps} />}
                            {section.key === 'employment' && <Employment {...sectionProps} />}
                            {section.key === 'documents' && <Documents {...sectionProps} />}
                            {section.key === 'payroll' && <Payroll {...sectionProps} />}
                            {section.key === 'family' && <Family {...sectionProps} />}
                            {section.key === 'qualifications' && <Qualifications {...sectionProps} />}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </>
                );
              }}
            </Formik>
            
            <ScrollToTop scrollRef={scrollRef} />
          </Box>          
        </Box>

        <DeleteConfirm
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={handleDelete}
          message={`Delete employee "${employee?.employeeName}"?`}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default EmployeeDetail;
